// web/src/components/CommandPalette.tsx — the ⌘K / Ctrl-K command palette (M4.4).
//
// A single, discoverable surface for navigation + the primary actions. Opens on
// Cmd/Ctrl-K (and "/" when not typing in a field); type to filter; Up/Down to move;
// Enter to run; Escape to close. Token-only, focus-trapped via the shared Modal-like
// overlay. Pure UI — every action is a callback handed in by the shell.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Vault,
  Inbox,
  KeyRound,
  ScrollText,
  ClipboardCheck,
  Settings as SettingsIcon,
  Plus,
  Sun,
  Moon,
  Users,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './ui/Icon';

export interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  /** Keywords broaden fuzzy matching beyond the label. */
  keywords?: string;
  run: () => void;
}

/** Build the default command set from the shell's affordances. */
export function useCommands({
  onNewSecret,
  onToggleTheme,
  theme,
  onSwitchIdentity,
}: {
  onNewSecret: () => void;
  onToggleTheme: () => void;
  theme: string;
  onSwitchIdentity: () => void;
}): Command[] {
  const navigate = useNavigate();
  return useMemo<Command[]>(
    () => [
      { id: 'new-secret', label: 'New secret', hint: 'Seal a secret', icon: Plus, keywords: 'seal create add', run: () => onNewSecret() },
      { id: 'go-vault', label: 'Go to Vault', hint: 'Records dashboard', icon: Vault, keywords: 'home records', run: () => navigate('/') },
      { id: 'go-proofs', label: 'Go to Shard proofs', hint: 'Prove you hold your shard', icon: Inbox, keywords: 'inbox possession proof shard liveness', run: () => navigate('/proofs') },
      { id: 'go-access', label: 'Go to My Access', hint: 'Shards you hold', icon: KeyRound, keywords: 'custody shards', run: () => navigate('/my-access') },
      { id: 'go-resp', label: 'Go to Responsibilities', hint: 'Records you own', icon: ClipboardCheck, keywords: 'owner transfer accountability', run: () => navigate('/responsibilities') },
      { id: 'go-audit', label: 'Go to Audit', hint: 'Tamper-evident timeline', icon: ScrollText, keywords: 'log verify chain', run: () => navigate('/audit') },
      { id: 'go-settings', label: 'Go to Settings', icon: SettingsIcon, keywords: 'members keys policies', run: () => navigate('/settings') },
      { id: 'theme', label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`, icon: theme === 'dark' ? Sun : Moon, keywords: 'dark light appearance', run: () => onToggleTheme() },
      { id: 'identity', label: 'Switch identity', hint: 'Acting as…', icon: Users, keywords: 'act as user persona', run: () => onSwitchIdentity() },
    ],
    [navigate, onNewSecret, onToggleTheme, theme, onSwitchIdentity],
  );
}

/** Global ⌘K / Ctrl-K (and "/") open hook. Ignores "/" while typing in a field. */
export function usePaletteHotkey(onOpen: () => void): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        onOpen();
        return;
      }
      if (e.key === '/' && !meta) {
        const el = document.activeElement;
        const typing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          (el instanceof HTMLElement && el.isContentEditable);
        if (!typing) {
          e.preventDefault();
          onOpen();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpen]);
}

function matches(c: Command, q: string): boolean {
  if (!q) return true;
  const hay = `${c.label} ${c.hint ?? ''} ${c.keywords ?? ''}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((tok) => hay.includes(tok));
}

export function CommandPalette({
  commands,
  onClose,
}: {
  commands: Command[];
  onClose: () => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => commands.filter((c) => matches(c, query)), [commands, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    setActive(0);
  }, [query]);

  function run(cmd: Command | undefined): void {
    if (!cmd) return;
    onClose();
    cmd.run();
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(filtered[active]);
    }
  }

  return createPortal(
    <div
      className="bgPaletteOverlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bgPalette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className="bgPaletteSearch">
          <Icon icon={Search} size="sm" tone="subtle" />
          <input
            ref={inputRef}
            className="bgPaletteInput"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or run a command"
            aria-label="Search or run a command"
            aria-controls="bg-palette-list"
            autoComplete="off"
          />
          <kbd className="bgKbd">esc</kbd>
        </div>
        <ul className="bgPaletteList" id="bg-palette-list" ref={listRef} role="listbox">
          {filtered.length === 0 ? (
            <li className="bgPaletteEmpty">No commands match "{query}"</li>
          ) : (
            filtered.map((c, i) => (
              <li key={c.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  className={`bgPaletteItem ${i === active ? 'bgPaletteItemActive' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(c)}
                >
                  <Icon icon={c.icon} size="sm" tone={i === active ? 'accent' : 'muted'} />
                  <span className="bgPaletteLabel">{c.label}</span>
                  {c.hint && <span className="bgPaletteHint">{c.hint}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

export default CommandPalette;
